import { UM_FILTER_CATALOG } from '../_components';
import type { FieldDef, SectionDef, SourceCategory } from './types';

/**
 * Extended « Filtres des bâtiments » catalogue: production fields (copied from UM_FILTER_CATALOG,
 * unchanged) + the attributes of the PO table (Name, PurposeSurfaceExplore and PurposeSurfaceLpdi removed by the PO). Placement follows
 * docs/product/features/um-filtres-batiments/ui-design.md. Lists come from urbanmap-app
 * (Shared/i18n/enum-labels.json); CRM lists are FICTITIOUS (no CRM data in any UrbanMap repo).
 */

const YES_NO = ['Indifférent', 'Oui', 'Non'];
const CONTRACT_PRESETS = ['< 6 mois', '< 1 an', '< 2 ans', 'Personnalisée'];
const CRM_PRESETS = ['3 derniers mois', '12 derniers mois', 'À venir', 'Personnalisée'];

const PROD_FIELDS: FieldDef[] = UM_FILTER_CATALOG.flatMap((s) =>
  s.fields.map((f): FieldDef => {
    switch (f.kind) {
      case 'perimeter':
        return { id: f.id, kind: 'perimeter', label: 'Périmètre', placeholder: f.placeholder };
      case 'segmented':
        return { id: f.id, kind: 'segmented', label: 'Mode de distance', options: f.options };
      case 'distance':
        return { id: f.id, kind: 'distance', label: f.label, max: f.max, step: f.step };
      case 'checkboxes':
        return { id: f.id, kind: 'checkboxes', label: f.label ?? 'Type de réseau', options: f.options };
      case 'multiselect':
      case 'radio':
        return { id: f.id, kind: f.kind, label: f.label, options: f.options };
      default:
        return { id: f.id, kind: f.kind, label: f.label };
    }
  }),
);

const n = (id: string, kind: FieldDef['kind'], label: string, source: SourceCategory, extra: Partial<FieldDef> = {}): FieldDef => ({
  id, kind, label, source, isNew: true, ...extra,
});

const NEW_FIELDS: FieldDef[] = [
  n('secondaryPurpose', 'toggle', "Inclure l'usage secondaire", 'Caractéristiques'),
  n('storeys', 'range', 'Nombre de niveaux', 'Caractéristiques'),
  n('renovationYear', 'range', 'Année de rénovation', 'Caractéristiques'),
  n('buildingDpe', 'dpe', 'DPE Bâtiment', 'Caractéristiques'),
  n('housingDpe', 'dpe', 'DPE Logements (au moins un logement en…)', 'Caractéristiques', { fullLabel: 'DPE Logements', multiValued: true }),

  n('selectedHeating', 'range', 'dont chauffage retenu (MWh)', 'Consommations (année standard)'),
  n('selectedHotWater', 'range', 'dont ECS retenue (MWh)', 'Consommations (année standard)'),
  n('heatingSystemType', 'text', "Type d'installation de chauffage", 'Caractéristiques', { placeholder: 'contient…' }),
  n('hotWaterSystemType', 'multiselect', "Type d'installation ECS", 'Caractéristiques', {
    options: ['Instantané', 'Semi-instantané', 'Semi-accumulation', 'Accumulation'],
  }),
  n('heatedSurface', 'range', 'Surface chauffée (m²)', 'Caractéristiques'),
  n('chimneyCount', 'range', 'Nombre de cheminées', 'Caractéristiques'),
  n('icpeBoiler', 'radio', 'Présence chaudière ICPE (2910)', 'Caractéristiques', { options: YES_NO }),
  n('hotClassification', 'radio', 'Dans zone de classement', 'Caractéristiques', { fullLabel: 'Dans zone de classement chaud', options: YES_NO }),
  n('hotInstallYear', 'range', "Année de l'installation", 'Données informationnelles', { fullLabel: "Année de l'installation chaud" }),
  n('hotContract', 'radio', "Contrat d'exploitation ENGIE renseigné", 'Données informationnelles', {
    fullLabel: "Contrat d'exploitation ENGIE chaud", options: YES_NO,
  }),
  n('hotExpiry', 'dateRange', 'Date échéance du contrat', 'Données informationnelles', { fullLabel: 'Date échéance du contrat chaud', presets: CONTRACT_PRESETS }),
  n('hotManager', 'text', 'Nom gestionnaire secondaire', 'Données informationnelles', { fullLabel: 'Nom gestionnaire secondaire chaud' }),

  n('cooledSurface', 'range', 'Surface refroidie (m²)', 'Caractéristiques'),
  n('coldClassification', 'radio', 'Dans zone de classement', 'Caractéristiques', { fullLabel: 'Dans zone de classement froid', options: YES_NO }),
  n('coldInstallYear', 'range', "Année de l'installation", 'Données informationnelles', { fullLabel: "Année de l'installation froid" }),
  n('coldContract', 'radio', "Contrat d'exploitation ENGIE renseigné", 'Données informationnelles', {
    fullLabel: "Contrat d'exploitation ENGIE froid", options: YES_NO,
  }),
  n('coldExpiry', 'dateRange', 'Date échéance du contrat', 'Données informationnelles', { fullLabel: 'Date échéance du contrat froid', presets: CONTRACT_PRESETS }),
  n('coldManager', 'text', 'Nom gestionnaire secondaire', 'Données informationnelles', { fullLabel: 'Nom gestionnaire secondaire froid' }),

  n('engieHeating', 'range', 'dont chauffage ENGIE (MWhu)', 'Données annuelles'),
  n('engieHotWater', 'range', 'dont ECS ENGIE (MWhu)', 'Données annuelles'),
  n('networkPdl', 'range', 'Nombre de PDL réseau', 'Données annuelles'),
  n('declaredYear', 'select', 'Année des données déclarées', 'Données annuelles', { options: ['2025', '2024', '2023'] }),
  n('declHeating', 'declared', 'Chauffage déclaré (MWh)', 'Données annuelles'),
  n('declHotWater', 'declared', 'ECS déclarée (MWh)', 'Données annuelles'),
  n('declHeatTotal', 'declared', 'Chaleur totale déclarée (MWh)', 'Données annuelles'),
  n('declColdTotal', 'declared', 'Froid total déclaré (MWh)', 'Données annuelles'),

  n('serviceType', 'multiselect', 'Type de prestation ENGIE', 'Données informationnelles', {
    options: ['00', 'P0R', 'P1', 'P2', 'P3G', 'P4', 'P5'], multiValued: true,
  }),
  n('marketType', 'multiselect', 'Type de marché ENGIE', 'Données informationnelles', {
    options: ['AT.AA', 'AT.AS', 'AT.RA', 'AT.RS', 'CP', 'CPI', 'MC', 'MCI', 'MF', 'MFI', 'MT', 'MTI', 'PF', 'PFI', 'SANS'], multiValued: true,
  }),
  n('installationName', 'text', 'Nom installation ENGIE', 'Données informationnelles', { placeholder: 'contient…', multiValued: true }),

  n('crmPresent', 'radio', 'Présent dans une offre', 'Données CRM', { options: YES_NO }),
  n('crmId', 'exact', "Id d'offre", 'Données CRM', { placeholder: 'Identifiant exact' }),
  n('crmName', 'text', "Nom de l'offre", 'Données CRM'),
  n('crmStage', 'multiselect', 'Étape', 'Données CRM', {
    fullLabel: "Étape de l'offre", options: ['Qualification', 'Étude', 'Proposition', 'Négociation', 'Gagnée', 'Perdue'],
  }),
  n('crmBusiness', 'text', 'Raison sociale du client', 'Données CRM'),
  n('crmContractObject', 'multiselect', 'Objet du contrat', 'Données CRM', {
    options: ['Exploitation', 'Fourniture de chaleur', 'Travaux', 'Performance énergétique', 'Raccordement réseau'],
  }),
  n('crmNetworkType', 'multiselect', 'Type de réseau', 'Données CRM', { fullLabel: "Type de réseau de l'offre", options: ['Chaud', 'Froid', 'Chaud et froid'] }),
  n('crmRecordType', 'multiselect', "Type d'enregistrement", 'Données CRM', { options: ['Opportunité', 'Avenant', 'Renouvellement'] }),
  n('crmHotWater', 'radio', 'Eau chaude', 'Données CRM', { fullLabel: "Eau chaude dans l'offre", options: YES_NO }),
  n('crmBuildings', 'range', "Nombre de bâtiments de l'offre", 'Données CRM'),
  n('crmCloseDate', 'dateRange', 'Date de clôture', 'Données CRM', { presets: CRM_PRESETS }),
  n('crmEffectiveDate', 'dateRange', "Date d'effet", 'Données CRM', { presets: CRM_PRESETS }),
  n('crmOwner', 'text', "Responsable de l'offre", 'Données CRM'),
  n('crmEntity', 'text', 'Entité commerciale', 'Données CRM'),
];

export const FIELDS: Record<string, FieldDef> = Object.fromEntries([...PROD_FIELDS, ...NEW_FIELDS].map((f) => [f.id, f]));

export const CRM_FIELD_IDS = NEW_FIELDS.filter((f) => f.source === 'Données CRM' && f.id !== 'crmPresent').map((f) => f.id);

export const fullLabel = (f: FieldDef) => f.fullLabel ?? f.label;

/** Variant A — sections by business question (pre-design « Structure cible »). */
export const SECTIONS_METIER: SectionDef[] = [
  { id: 'perimeter', title: 'Périmètre', defaultOpen: true, groups: [{ fields: ['perimeter'] }] },
  { id: 'identification', title: 'Identification & gestion', defaultOpen: true, groups: [{ fields: ['owner', 'ownerType', 'syndic'] }] },
  {
    id: 'characteristics',
    title: 'Caractéristiques des bâtiments',
    defaultOpen: true,
    groups: [
      { title: 'Usages', fields: ['usages', 'secondaryPurpose'] },
      { title: 'Dimensions', fields: ['dwellings', 'storeys', 'area'] },
      { title: 'Âge', fields: ['year', 'renovationYear'] },
      { title: 'Performance', fields: ['buildingDpe', 'housingDpe'] },
    ],
  },
  {
    id: 'heat',
    title: 'Chaud — énergie, installation, exploitation',
    groups: [
      { title: 'Consommations', fields: ['heatConsumption', 'selectedHeating', 'selectedHotWater'] },
      {
        title: 'Installation',
        fields: ['heatingEnergy', 'hotWaterEnergy', 'heatingType', 'heatingSystemType', 'hotWaterSystemType', 'heatedSurface', 'chimneyCount', 'icpeBoiler', 'hotClassification'],
      },
      { title: 'Exploitation', fields: ['hotInstallYear', 'hotContract', 'hotExpiry', 'hotManager'] },
    ],
  },
  {
    id: 'cold',
    title: 'Froid — énergie, installation, exploitation',
    groups: [
      { title: 'Consommations', fields: ['coldConsumption'] },
      { title: 'Installation', fields: ['coolingEnergy', 'coolingType', 'coolingInstallation', 'cooledSurface', 'fans', 'icpe2921', 'coldClassification'] },
      { title: 'Exploitation', fields: ['coldInstallYear', 'coldContract', 'coldExpiry', 'coldManager'] },
    ],
  },
  { id: 'distance', title: 'Distance aux réseaux', groups: [{ fields: ['distanceMode', 'networkType', 'distance'] }] },
  {
    id: 'sources',
    title: 'Sources de consommations',
    groups: [
      { title: 'ENGIE', fields: ['hasEngie', 'engieConsumption', 'engieHeating', 'engieHotWater', 'engiePdl'] },
      { title: 'GRDF', fields: ['hasGrdf', 'grdfConsumption', 'grdfPdl'] },
      { title: 'ENEDIS', fields: ['hasEnedis', 'enedisConsumption', 'enedisPdl'] },
      { title: 'DLE Réseaux', fields: ['hasDleNetwork', 'dleHeat', 'dleCold', 'networkPdl'] },
      { title: 'Consommations déclarées', fields: ['declaredYear', 'declHeating', 'declHotWater', 'declHeatTotal', 'declColdTotal'] },
    ],
  },
  {
    id: 'engie-crm',
    title: 'Activité ENGIE & offres CRM',
    groups: [
      { title: 'Installations ENGIE', fields: ['serviceType', 'marketType', 'installationName'] },
      { title: 'Offres CRM', fields: ['crmPresent', ...CRM_FIELD_IDS] },
    ],
  },
];

const SOURCE_ORDER: SourceCategory[] = [
  'Identification', 'Caractéristiques', 'Consommations (année standard)', 'Données informationnelles', 'Données annuelles', 'Données CRM',
];

/** Variant B — production sections untouched, new fields appended in the PO table categories. */
export const SECTIONS_SOURCE: SectionDef[] = [
  ...UM_FILTER_CATALOG.map((s): SectionDef => ({ id: s.id, title: s.title, defaultOpen: s.defaultOpen, groups: [{ fields: s.fields.map((f) => f.id) }] })),
  ...SOURCE_ORDER.map((cat): SectionDef => ({
    id: `source-${cat}`,
    title: cat,
    groups: [{ fields: NEW_FIELDS.filter((f) => f.source === cat).map((f) => f.id) }],
  })),
];

export const NEW_FIELD_COUNT = NEW_FIELDS.length;
